import React, { lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { Check, X, Sparkles, ShieldCheck, RotateCcw, Star, ChevronDown, ArrowRight } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
import { CountdownBanner } from './CountdownBanner';
import { useCurrency } from '../hooks/useCurrency';
interface Tier {
  id: string;
  name: string;
  tagline: string;
  priceMXN: number;
  priceUSD: number;
  originalPriceMXN: number;
  originalPriceUSD: number;
  discount: string;
  highlighted: boolean;
  badge?: string;
  image: string;
  checkoutUrl: string;
  description: string;
  features: {
    text: string;
    included: boolean;
  }[];
  cta: string;
}
const tiers: Tier[] = [{
  id: 'completo',
  name: 'Intermedio',
  tagline: 'La experiencia completa para transformarte',
  priceMXN: 7.99,
  priceUSD: 7.99,
  originalPriceMXN: 44.99,
  originalPriceUSD: 44.99,
  discount: '-82%',
  highlighted: true,
  badge: 'Más Popular',
  image: 'https://cdn.magicpatterns.com/uploads/6ebQqnFDhBcg32yfSmKiS1/eboo2Baudiobook2B3bonus.png',
  checkoutUrl: 'https://pay.hotmart.com/G104442978T',
  description: 'Hábitos Atómicos Espirituales es un kit digital para dejar de empezar y por fin terminar. Incluye eBook + audiolibro + 3 bonos. Basado en el Método ANCLA y el Pacto de 5 Minutos: constancia diaria con Dios, sin culpa y aplicable en vida real.',
  features: [{
    text: 'eBook: Hábitos Atómicos Espirituales',
    included: true
  }, {
    text: 'Habit Tracker (imprimible + digital)',
    included: true
  }, {
    text: '3 Bonos',
    included: true
  }, {
    text: 'Audiolibro completo',
    included: true
  }, {
    text: 'Devocional de 30 Días',
    included: false
  }],
  cta: 'Quiero el paquete completo'
}, {
  id: 'esencial',
  name: 'Básico',
  tagline: 'Para empezar con lo fundamental',
  priceMXN: 7.99,
  priceUSD: 7.99,
  originalPriceMXN: 34.8,
  originalPriceUSD: 34.8,
  discount: '-77%',
  highlighted: false,
  image: 'https://cdn.magicpatterns.com/uploads/9w1BmRKj86iqnwGw1ygiuY/habitos_atomicos_espi.png',
  checkoutUrl: 'https://pay.hotmart.com/S104255087F?checkoutMode=10',
  description: 'Descubre nuestra exclusiva colección de recursos digitales diseñados para impulsar tu crecimiento personal, financiero y espiritual. Ofrecemos una amplia gama de ebooks, audiolibros y herramientas prácticas como trackers de hábitos y guías especializadas, cuidadosamente seleccionados para ayudarte a alcanzar tus objetivos y transformar tu vida. Ideal para emprendedores, profesionales, coaches y estudiantes que buscan mejorar su productividad, gestionar sus finanzas de manera efectiva y cultivar su bienestar integral.',
  features: [{
    text: 'eBook: Hábitos Atómicos Espirituales',
    included: true
  }, {
    text: 'Habit Tracker (imprimible + digital)',
    included: true
  }, {
    text: 'Bono: Reinicio Express',
    included: true
  }, {
    text: 'Audiolibro completo',
    included: false
  }, {
    text: 'Devocional de 30 Días',
    included: false
  }],
  cta: 'Empezar con lo esencial'
}];
export function PricingSection() {
  const {
    currency
  } = useCurrency();
  const handlePurchaseClick = (url: string, productName: string, price: number) => {
    trackAddToCart(productName, price, currency);
    window.open(url, '_blank');
  };
  const scrollToDetails = (id: string) => {
    const element = document.getElementById(`details-${id}`);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const getPrice = (tier: Tier) => 7.99;
  const getOriginalPrice = (tier: Tier) => tier.originalPriceUSD;
  return <section id="pricing-section" className="py-16 md:py-28 bg-[#050b14] relative overflow-hidden" data-id="element-258">
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#d4af37]/3 rounded-full blur-[120px]" data-id="element-259" />

      <div className="max-w-6xl mx-auto px-5 md:px-6 relative z-10" data-id="element-260">
        <FadeIn data-id="element-261">
          <div className="text-center mb-6 md:mb-8" data-id="element-262">
            <span className="text-[#d4af37] tracking-widest uppercase text-xs md:text-sm font-semibold mb-3 block" data-id="element-263">
              Elige tu plan
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-5" data-id="element-264">
              Dos caminos,{' '}
              <span className="text-[#d4af37] italic" data-id="element-265">un mismo destino</span>
            </h2>
            <p className="text-base md:text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-266">
              Elige el nivel de apoyo que necesitas. Ambos incluyen el sistema
              ANCLA completo.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-267">
          <div className="flex justify-center mb-8 md:mb-12" data-id="element-268">
            <CountdownBanner data-id="element-269" />
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-5 md:gap-8 items-start mb-16 md:mb-20 max-w-4xl mx-auto" data-id="element-270">
          {tiers.map((tier, index) => <FadeIn key={tier.id} delay={index * 0.03} data-id="element-271">
              <div className={`relative flex flex-col h-full rounded-2xl overflow-hidden ${tier.highlighted ? 'bg-gradient-to-b from-[#1e3a5f]/30 to-[#0a1628] border-2 border-[#d4af37] shadow-lg md:shadow-[0_0_40px_rgba(212,175,55,0.15)] md:-mt-4 md:mb-[-16px] z-10' : 'bg-[#0a1628] border border-[#1e3a5f]'}`} data-id="element-272">
                {tier.badge && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20" data-id="element-273">
                    <div className={`flex items-center gap-1.5 ${tier.highlighted ? 'bg-[#d4af37] text-[#0a1628]' : 'bg-[#1e3a5f] text-[#d4af37] border border-[#d4af37]/30'} text-xs font-bold px-4 py-1.5 rounded-full shadow-lg`} data-id="element-274">
                      <Star className="w-3.5 h-3.5" data-id="element-275" />
                      <span data-id="element-276">{tier.badge}</span>
                    </div>
                  </div>}

                <div className="w-full aspect-[4/3] bg-[#0f1f38] relative overflow-hidden" data-id="element-277">
                  <img src={tier.image} alt={tier.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-278" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] to-transparent opacity-60" data-id="element-279" />
                </div>

                <div className="p-5 md:p-8 flex flex-col flex-1" data-id="element-280">
                  <div className="mb-3 md:mb-4" data-id="element-281">
                    <h3 className={`text-xl md:text-2xl font-bold mb-1 ${tier.highlighted ? 'text-[#d4af37]' : 'text-white'}`} data-id="element-282">
                      {tier.name}
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-283">{tier.tagline}</p>
                  </div>

                  <div className="mb-5 md:mb-6" data-id="element-284">
                    <div className="flex items-center gap-2 mb-1" data-id="element-285">
                      <span className="text-gray-500 line-through text-base" data-id="element-286">
                        ${getOriginalPrice(tier)} USD
                      </span>
                      <span className="bg-red-500/20 text-red-300 text-[10px] font-bold px-1.5 py-0.5 rounded" data-id="element-287">
                        {tier.discount}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1" data-id="element-288">
                      <span className={`text-4xl md:text-5xl font-bold ${tier.highlighted ? 'text-[#d4af37]' : 'text-white'}`} data-id="element-289">
                        $7.99
                      </span>
                      <span className={`${tier.highlighted ? 'text-[#d4af37]/60' : 'text-gray-400'} text-sm`} data-id="element-290">
                        USD
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-291">Pago único</p>
                  </div>

                  <Button id={tier.id === 'completo' ? 'cta-intermedio' : undefined} variant={tier.highlighted ? 'primary' : 'outline'} className={`w-full py-4 mb-3 ${tier.highlighted ? 'btn-shimmer' : ''}`} onClick={() => handlePurchaseClick(tier.checkoutUrl, tier.name, getPrice(tier))} data-id="element-292">
                    {tier.cta}
                  </Button>

                  <button onClick={() => scrollToDetails(tier.id)} className="flex items-center justify-center gap-2 text-sm text-gray-400 active:text-[#d4af37] md:hover:text-[#d4af37] transition-colors w-full py-2" data-id="element-293">
                    <span data-id="element-294">Ver lo que incluye</span>
                    <ChevronDown className="w-4 h-4" data-id="element-295" />
                  </button>
                </div>
              </div>
            </FadeIn>)}
        </div>

        <div className="space-y-12 md:space-y-24" data-id="element-296">
          <FadeIn data-id="element-297">
            <div className="text-center mb-10 md:mb-12" data-id="element-298">
              <h3 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-299">
                Detalle de cada Pack
              </h3>
              <div className="w-24 h-1 bg-[#d4af37] mx-auto rounded-full" data-id="element-300" />
            </div>
          </FadeIn>

          {tiers.map((tier, index) => <div key={tier.id} id={`details-${tier.id}`} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 md:gap-16 items-center scroll-mt-24`} data-id="element-301">
              <FadeIn delay={0.05} className="w-full md:w-1/2" data-id="element-302">
                <div className="relative rounded-2xl overflow-hidden border border-[#1e3a5f] shadow-xl" data-id="element-303">
                  <img src={tier.image} alt={tier.name} loading="lazy" decoding="async" className="w-full h-auto object-cover" data-id="element-304" />
                  <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 bg-[#0a1628]/95 md:bg-[#0a1628]/90 border border-[#d4af37]/30 px-3 py-1.5 md:px-4 md:py-2 rounded-lg" data-id="element-305">
                    <p className="text-[#d4af37] font-bold text-lg md:text-xl" data-id="element-306">
                      $7.99 USD
                    </p>
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={0.1} className="w-full md:w-1/2" data-id="element-307">
                <div className="space-y-5 md:space-y-6" data-id="element-308">
                  <div data-id="element-309">
                    <span className="text-[#d4af37] text-sm font-bold tracking-widest uppercase mb-2 block" data-id="element-310">
                      {tier.name}
                    </span>
                    <h4 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4" data-id="element-311">
                      {tier.tagline}
                    </h4>
                    <p className="text-gray-300 text-sm md:text-lg leading-relaxed" data-id="element-312">
                      {tier.description}
                    </p>
                  </div>

                  <div className="bg-[#1e3a5f]/20 rounded-xl p-5 md:p-6 border border-[#1e3a5f]" data-id="element-313">
                    <h5 className="text-white font-semibold mb-3 md:mb-4 flex items-center gap-2" data-id="element-314">
                      <Sparkles className="w-4 h-4 text-[#d4af37]" data-id="element-315" />
                      Lo que incluye este pack:
                    </h5>
                    <ul className="space-y-2.5 md:space-y-3" data-id="element-316">
                      {tier.features.map((feature, i) => <li key={i} className="flex items-start gap-2.5 md:gap-3" data-id="element-317">
                          {feature.included ? <Check className="w-4 h-4 md:w-5 md:h-5 text-[#d4af37] flex-shrink-0 mt-0.5" data-id="element-318" /> : <X className="w-4 h-4 md:w-5 md:h-5 text-gray-600 flex-shrink-0 mt-0.5" data-id="element-319" />}
                          <span className={`${feature.included ? 'text-gray-200' : 'text-gray-500'} text-sm md:text-base`} data-id="element-320">
                            {feature.text}
                          </span>
                        </li>)}
                    </ul>
                  </div>

                  <Button variant={tier.highlighted ? 'primary' : 'outline'} className="w-full md:w-auto px-8" onClick={() => handlePurchaseClick(tier.checkoutUrl, tier.name, getPrice(tier))} data-id="element-321">
                    Adquirir {tier.name} <ArrowRight className="w-4 h-4 ml-2" data-id="element-322" />
                  </Button>
                </div>
              </FadeIn>
            </div>)}
        </div>

        <FadeIn delay={0.1} data-id="element-323">
          <div className="mt-16 md:mt-32 max-w-xl mx-auto" data-id="element-324">
            <div className="bg-[#1e3a5f]/20 border border-[#1e3a5f] rounded-xl p-4 md:p-6" data-id="element-325">
              <div className="flex items-start gap-3" data-id="element-326">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#d4af37]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-327">
                  <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-[#d4af37]" data-id="element-328" />
                </div>
                <div className="text-left" data-id="element-329">
                  <p className="text-white font-bold text-sm md:text-base mb-1" data-id="element-330">
                    Garantía de 7 días en todos los planes
                  </p>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed" data-id="element-331">
                    Si en 7 días sientes que esto no te sirvió, te devolvemos
                    cada peso. Sin preguntas, sin drama. El riesgo es nuestro,
                    no tuyo.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-gray-400 text-xs md:text-sm mt-4" data-id="element-332">
              <ShieldCheck className="w-4 h-4 text-[#d4af37]" data-id="element-333" />
              <span data-id="element-334">
                Pago seguro con Hotmart • Aceptamos todas las tarjetas
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}